import { cn } from "@bafe/ui";
import type { ReactNode } from "react";
import { Link } from "react-router";

export type LinkButtonVariant = "primary" | "secondary" | "ghost";
export type LinkButtonSize = "sm" | "md" | "lg";

/** Mirrors `Button` so a navigation control and an action control match. */
const VARIANTS: Record<LinkButtonVariant, string> = {
	primary: "bg-(--color-brand-600) text-white hover:bg-(--color-brand-700) active:bg-(--color-brand-800)",
	secondary:
		"bg-(--surface) text-(--text-primary) border border-(--border-strong) hover:bg-(--surface-sunken) active:bg-(--color-ink-100)",
	ghost: "bg-transparent text-(--text-secondary) hover:bg-(--surface-sunken) hover:text-(--text-primary)",
};

const SIZES: Record<LinkButtonSize, string> = {
	sm: "h-9 px-3 text-sm gap-1.5",
	md: "h-10 px-4 text-sm gap-2",
	lg: "h-11 px-5 text-base gap-2",
};

export interface LinkButtonProps {
	to: string;
	variant?: LinkButtonVariant;
	size?: LinkButtonSize;
	iconLeft?: ReactNode;
	iconRight?: ReactNode;
	fullWidth?: boolean;
	className?: string;
	children: ReactNode;
	replace?: boolean;
}

export function LinkButton({
	to,
	variant = "secondary",
	size = "md",
	iconLeft,
	iconRight,
	fullWidth = false,
	className,
	children,
	replace,
}: LinkButtonProps) {
	return (
		<Link
			to={to}
			replace={replace}
			className={cn(
				"inline-flex items-center justify-center rounded-(--radius-md) font-medium transition-colors duration-150",
				VARIANTS[variant],
				SIZES[size],
				fullWidth && "w-full",
				className,
			)}
		>
			{iconLeft}
			<span>{children}</span>
			{iconRight}
		</Link>
	);
}
